import { api } from './api';

/**
 * Relatório de e-mails disparados pela gamificação.
 *
 * Lê apenas o espelho que o scheduler `mailersend_sync` mantém na plataforma —
 * nenhuma credencial da MailerSend passa pelo browser. As consultas usam
 * aggregates preparados (`POST /find/<id>`) em vez de pipelines montados no
 * cliente: o que pode ser consultado fica fixo no servidor e o token do admin
 * precisa apenas do escopo `write_find_<id>`.
 */

export const EMAIL_EVENTS = [
  'queued',
  'sent',
  'delivered',
  'opened',
  'clicked',
  'deferred',
  'soft_bounced',
  'hard_bounced',
  'spam_complaints',
  'unsubscribed',
] as const;

export type EmailEvent = (typeof EMAIL_EVENTS)[number];

/** Documento diário de `mailersend_daily__c`. `_id` é a data em `YYYY-MM-DD`. */
export interface EmailDailyStat {
  _id: string;
  queued: number;
  sent: number;
  delivered: number;
  opened: number;
  opened_unique: number;
  clicked: number;
  clicked_unique: number;
  soft_bounced: number;
  hard_bounced: number;
  spam_complaints: number;
  unsubscribed: number;
}

/** Evento individual de `mailersend_activity__c`. */
export interface EmailActivity {
  _id: string;
  email_id: string;
  sent_at: string;
  subject: string;
  recipient: string;
  event_at: string;
  event: EmailEvent;
  status: string;
}

export interface EmailSyncStatus {
  activities: number;
  days: number;
  ok: boolean;
  message: string;
  synced_at: string;
}

/** Janela de datas do filtro, em `YYYY-MM-DD`. */
export interface EmailDateRange {
  from: string;
  to: string;
}

export interface EmailActivityFilters extends EmailDateRange {
  /** `null` = todos os eventos. */
  event: EmailEvent | null;
  /** Busca parcial, case-insensitive. `''` = todos os destinatários. */
  recipient: string;
}

export interface EmailActivityPage {
  rows: EmailActivity[];
  total: number;
}

export const getEmailDailyStats = async ({ from, to }: EmailDateRange) => {
  const response = await api.post('/find/mailersend_daily', { from, to });
  return (response.data ?? []) as EmailDailyStat[];
};

/**
 * Parâmetros de filtro compartilhados pela página e pela contagem — os dois
 * aggregates precisam casar exatamente o mesmo conjunto de documentos.
 */
const buildActivityFilterParams = (filters: EmailActivityFilters) => ({
  // `event_at` é string ISO-8601, então a janela é comparada lexicograficamente.
  // `".000000Z"` < `"Z"`, logo o limite superior sem milissegundos inclui o dia inteiro.
  from: `${filters.from}T00:00:00`,
  to: `${filters.to}T23:59:59Z`,
  // Um `$param` ausente vira `null` no servidor e o `$match` deixa de casar
  // qualquer documento. Por isso "todos" é a lista completa, nunca `null`.
  events: filters.event ? [filters.event] : [...EMAIL_EVENTS],
  recipient: filters.recipient,
});

export const getEmailActivity = async (
  filters: EmailActivityFilters,
  page: number,
  pageSize: number,
): Promise<EmailActivityPage> => {
  const filterParams = buildActivityFilterParams(filters);

  // Duas consultas em paralelo, e não um `$facet`: dentro de um `$facet` o `$sort`
  // não é servido por índice, então o Mongo varre todo o conjunto filtrado e ordena
  // em memória só para devolver uma página. Separada, a consulta de linhas usa o
  // índice `event_at_-1` e o `$limit` corta a leitura no tamanho da página.
  const [rowsResponse, countResponse] = await Promise.all([
    api.post('/find/mailersend_activity', { ...filterParams, skip: (page - 1) * pageSize, limit: pageSize }),
    api.post('/find/mailersend_activity_count', filterParams),
  ]);

  // `$count` não emite documento algum quando nada casa o filtro.
  const [count] = (countResponse.data ?? []) as { total: number }[];

  return { rows: (rowsResponse.data ?? []) as EmailActivity[], total: count?.total ?? 0 };
};

export const getEmailSyncStatus = async () => {
  const response = await api.get('/database/mailersend_sync__c/status');
  return response.data as EmailSyncStatus;
};
